import { Reveal } from "./reveal";
import tan from "../assets/stamped/stamped-tan-flagstone.webp.asset.json";
import gray from "../assets/stamped/stamped-gray-slate.webp.asset.json";
import brown from "../assets/stamped/stamped-brown-patio.webp.asset.json";
import antique from "../assets/stamped/stamped-antique-restore.webp.asset.json";
import crew from "../assets/concrete-job/crew.webp.asset.json";
import finishedOne from "../assets/concrete-job/patio-finished-1.webp.asset.json";
import finishedTwo from "../assets/concrete-job/patio-finished-2.webp.asset.json";
import projectVideo from "../assets/concrete-job/project-video.mp4.asset.json";
import stampedFinished from "../assets/concrete-job/stamped-concrete-finished.webp.asset.json";
import concreteFinished from "../assets/concrete-job/concrete-cleaning-finished.webp.asset.json";
import dirtyVideo from "../assets/concrete-job/dirty-stamped-concrete.mp4.asset.json";
import dirtyPoster from "../assets/concrete-job/dirty-stamped-concrete-poster.webp.asset.json";
import antiquingVideo from "../assets/concrete-job/antiquing-applied.mp4.asset.json";
import antiquingPoster from "../assets/concrete-job/antiquing-applied-poster.webp.asset.json";
import dryVideo from "../assets/concrete-job/antiquing-dry.mp4.asset.json";
import dryPoster from "../assets/concrete-job/antiquing-dry-poster.webp.asset.json";
import finalPhoto from "../assets/concrete-job/stamped-concrete-final.webp.asset.json";

const PROJECT_STEPS = [
  { src: dirtyVideo.url, poster: dirtyPoster.url, label: "Step 1", caption: "The dirty stamped concrete before restoration" },
  { src: antiquingVideo.url, poster: antiquingPoster.url, label: "Step 2", caption: "After the antiquing color was applied" },
  { src: dryVideo.url, poster: dryPoster.url, label: "Step 3", caption: "The antiquing after it dried" },
  { src: projectVideo.url, poster: finishedOne.url, label: "Step 4", caption: "The finished surface after sealing" },
];

const SHOTS = [
  { src: finalPhoto.url, alt: "Final dark gray stamped concrete patio after antiquing and sealing", caption: "Final product — antiqued and sealed stamped concrete" },
  { src: crew.url, alt: "Capital Pro crew beside a finished dark gray stamped concrete patio", caption: "Capital Pro crew on site with the finished stamped patio" },
  { src: stampedFinished.url, alt: "Close view of a finished dark gray stamped concrete patio", caption: "Stamped concrete — cleaned and sealed" },
  { src: concreteFinished.url, alt: "Finished standard concrete patio after professional cleaning", caption: "Standard concrete — professionally cleaned" },
  { src: finishedOne.url, alt: "Finished dark gray stamped concrete patio beside a Capital Region home", caption: "Finished stamped concrete patio — cleaned and sealed" },
  { src: finishedTwo.url, alt: "Wide view of a completed dark gray stamped concrete patio project", caption: "Another view of the completed patio and restored finish" },
  { src: tan.url, alt: "Tan flagstone stamped concrete patio sealed with a wet-look sealer", caption: "Flagstone stamped patio — cleaned, antiqued & sealed" },
  { src: gray.url, alt: "Gray slate stamped concrete patio after cleaning and sealing", caption: "Gray slate stamp — color depth brought back with a fresh seal" },
  { src: brown.url, alt: "Brown stamped concrete patio and steps freshly sealed", caption: "Stamped patio & steps — full clean, antique and reseal" },
  { src: antique.url, alt: "Stamped concrete patio mid-project showing faded concrete next to restored antiqued color", caption: "Antiquing in progress — faded slab on one side, restored color on the other" },
];

/** Stamped concrete sealing & antiquing photo gallery. */
export function StampedGallerySection() {
  return (
    <section className="bg-white py-16 md:py-20">
      <div className="mx-auto max-w-[1200px] px-4">
        <div className="text-center mb-10">
          <p
            className="text-[var(--brand-red)] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Stamped Concrete
          </p>
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Sealing & Antiquing That Brings The Color Back
          </h2>
          <p className="mt-4 mx-auto max-w-[680px] text-[var(--brand-charcoal)]/80 leading-relaxed">
            Faded, chalky stamped concrete doesn't need to be replaced. We clean it, re-apply
            antiquing color to restore the pattern contrast, then lock it in with a premium sealer.
          </p>
        </div>

        <div className="mb-8 grid gap-5 sm:grid-cols-2">
          {PROJECT_STEPS.map((step) => (
            <Reveal key={step.src} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <video
                controls
                playsInline
                preload="metadata"
                poster={step.poster}
                aria-label={`${step.label}: ${step.caption}`}
                className="aspect-video w-full bg-[var(--brand-charcoal)] object-cover"
              >
                <source src={step.src} type="video/mp4" />
                Your browser does not support embedded video.
              </video>
              <p className="px-4 py-3 text-sm text-[var(--brand-charcoal)]/80">
                <strong className="text-[var(--brand-deep-blue)]">{step.label}:</strong> {step.caption}
              </p>
            </Reveal>
          ))}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          {SHOTS.map((s) => (
            <Reveal key={s.src} className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                decoding="async"
                width={960}
                height={720}
                className="aspect-[4/3] w-full object-cover"
              />
              <p className="px-4 py-3 text-sm text-[var(--brand-charcoal)]/80">{s.caption}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
